import type { LocalizedText } from "@/math";

export type ItemKind = "consumable" | "weapon" | "armor" | "accessory" | "key";
export type EquipSlot = "weapon" | "armor" | "accessory";

export interface ItemStats {
  atk?: number;
  def?: number;
  /** Extra seconds on every question timer. */
  timerBonusSec?: number;
  /** Free hints per battle. */
  freeHints?: number;
}

export interface ItemDef {
  id: string;
  kind: ItemKind;
  name: LocalizedText;
  description: LocalizedText;
  icon: string;
  /** Shop price in gold; undefined = not for sale. */
  price?: number;
  heal?: number;
  hint?: boolean;
  stats?: ItemStats;
}

export const ITEMS: Record<string, ItemDef> = {
  potion: {
    id: "potion",
    kind: "consumable",
    name: { pl: "Mikstura", en: "Potion" },
    description: { pl: "Przywraca 15 HP.", en: "Restores 15 HP." },
    icon: "🧪",
    price: 8,
    heal: 15,
  },
  big_potion: {
    id: "big_potion",
    kind: "consumable",
    name: { pl: "Duża mikstura", en: "Big potion" },
    description: { pl: "Przywraca 40 HP.", en: "Restores 40 HP." },
    icon: "⚗️",
    price: 20,
    heal: 40,
  },
  hint_scroll: {
    id: "hint_scroll",
    kind: "consumable",
    name: { pl: "Zwój podpowiedzi", en: "Hint scroll" },
    description: {
      pl: "W walce usuwa dwie błędne odpowiedzi albo pokazuje pierwszy krok rozwiązania.",
      en: "In battle removes two wrong answers or shows the first step of the solution.",
    },
    icon: "📜",
    price: 12,
    hint: true,
  },
  sword_wood: {
    id: "sword_wood",
    kind: "weapon",
    name: { pl: "Drewniany miecz", en: "Wooden sword" },
    description: { pl: "Na początek wystarczy.", en: "Good enough to start with." },
    icon: "🗡️",
    stats: { atk: 0 },
  },
  sword_iron: {
    id: "sword_iron",
    kind: "weapon",
    name: { pl: "Żelazny miecz", en: "Iron sword" },
    description: { pl: "+3 do ataku.", en: "+3 attack." },
    icon: "⚔️",
    price: 60,
    stats: { atk: 3 },
  },
  tunic_cloth: {
    id: "tunic_cloth",
    kind: "armor",
    name: { pl: "Płócienna tunika", en: "Cloth tunic" },
    description: { pl: "Lekka i wygodna.", en: "Light and comfortable." },
    icon: "👕",
    stats: { def: 0 },
  },
  leather_vest: {
    id: "leather_vest",
    kind: "armor",
    name: { pl: "Skórzana kamizelka", en: "Leather vest" },
    description: { pl: "Potwory zadają 1 obrażenie mniej.", en: "Monsters deal 1 less damage." },
    icon: "🦺",
    price: 45,
    stats: { def: 1 },
  },
  wand_focus: {
    id: "wand_focus",
    kind: "accessory",
    name: { pl: "Różdżka Skupienia", en: "Wand of Focus" },
    description: { pl: "+5 sekund na każde pytanie.", en: "+5 seconds on every question." },
    icon: "🪄",
    stats: { timerBonusSec: 5 },
  },
  sage_glasses: {
    id: "sage_glasses",
    kind: "accessory",
    name: { pl: "Okulary Mędrca", en: "Sage's Glasses" },
    description: { pl: "Jedna darmowa podpowiedź w każdej walce.", en: "One free hint in every battle." },
    icon: "👓",
    stats: { freeHints: 1 },
  },
  sword_steel: {
    id: "sword_steel",
    kind: "weapon",
    name: { pl: "Stalowy miecz", en: "Steel sword" },
    description: {
      pl: "+6 do ataku. Kuty na Wybrzeżu Konserw.",
      en: "+6 attack. Forged on the Cannery Coast.",
    },
    icon: "🗡️",
    stats: { atk: 6 },
  },
  chainmail: {
    id: "chainmail",
    kind: "armor",
    name: { pl: "Kolczuga", en: "Chainmail" },
    description: { pl: "Potwory zadają 2 obrażenia mniej.", en: "Monsters deal 2 less damage." },
    icon: "🛡️",
    stats: { def: 2 },
  },
  amulet_time: {
    id: "amulet_time",
    kind: "accessory",
    name: { pl: "Amulet Czasu", en: "Amulet of Time" },
    description: { pl: "+8 sekund na każde pytanie.", en: "+8 seconds on every question." },
    icon: "⏳",
    stats: { timerBonusSec: 8 },
  },
  plank: {
    id: "plank",
    kind: "key",
    name: { pl: "Deska z mostu", en: "Bridge plank" },
    description: { pl: "Odzyskana od chochlika.", en: "Recovered from an imp." },
    icon: "🪵",
  },
};

export function getItem(id: string): ItemDef {
  const def = ITEMS[id];
  if (!def) throw new Error(`Unknown item: ${id}`);
  return def;
}

export function slotOf(item: ItemDef): EquipSlot | null {
  return item.kind === "weapon" || item.kind === "armor" || item.kind === "accessory" ? item.kind : null;
}
